// Label e helper di stato/ruolo riutilizzati in tutta la UI V2.
// Le label "umane" passano sempre da i18next (chiavi in src-v2/locales/*).
// Le chiavi DB restano in italiano nei valori delle costanti (POR, founder,
// present, ecc.) e vengono mappate alle traduzioni al momento del render.

import i18n from "./i18n";
import type { ResponseStatus } from "../types";

// Helper: legge una key namespaced dal common.json corrente. Fallback al
// raw key se la traduzione manca (evita "??chiave??" nella UI).
function tr(key: string, fallback?: string): string {
  const v = i18n.t(key);
  if (typeof v === "string" && v && v !== key) return v;
  return fallback ?? key;
}

export function teamRoleLabel(role: string | null | undefined): string {
  const normalized = (role || "player").toLowerCase();
  // Legacy values: owner -> founder, admin/staff -> coach, captain -> player.
  const mapped =
    normalized === "owner" ? "founder"
    : normalized === "admin" || normalized === "staff" ? "coach"
    : normalized === "captain" ? "player"
    : normalized;
  return tr(`roles.${mapped}`, role || "");
}

export function canManageTeam(role: string): boolean {
  return ["founder", "coach", "owner", "admin", "staff"].includes(role);
}

export function isFounderRole(role: string): boolean {
  return role === "founder" || role === "owner";
}

export function statusLabel(status: ResponseStatus): string {
  return tr(`response.${status}`, status);
}

export const statusBadgeClass: Record<ResponseStatus, string> = {
  present: "bg-success text-success-foreground hover:bg-success",
  late: "bg-warning text-warning-foreground hover:bg-warning",
  absent: "bg-destructive text-destructive-foreground hover:bg-destructive",
  no_response: "bg-muted text-muted-foreground hover:bg-muted",
};

// Label umana per la piattaforma EA Pro Clubs (common-gen5 = Next gen, common-gen4 = Old gen).
export function eaPlatformLabel(platform: string | null | undefined): string {
  if (platform === "common-gen5") return tr("eaPlatform.common-gen5", "Next gen");
  if (platform === "common-gen4") return tr("eaPlatform.common-gen4", "Old gen");
  return platform || tr("eaPlatform.unknown", "—");
}

// Set chiuso dei ruoli giocatore (vincolato a livello DB da
// players_role_code_check). 11 ruoli classici del calcio italiano.
export const PLAYER_ROLES = [
  "POR",
  "TD",
  "DC",
  "TS",
  "MED",
  "CC",
  "TRQ",
  "ED",
  "ES",
  "ATT",
  "SP",
] as const;
export type PlayerRoleCode = (typeof PLAYER_ROLES)[number];

export function playerRoleLabel(code: PlayerRoleCode | string | null | undefined): string {
  if (!code) return "";
  return tr(`playerRoles.${code}`, code);
}

// Pesatura del ruolo in tabelle/sezioni: ATT < CC < DIF < GK < altri.
const ROLE_PRIORITY: Record<PlayerRoleCode, number> = {
  ATT: 1,
  SP: 1,
  ED: 1,
  ES: 1,
  TRQ: 2,
  CC: 2,
  MED: 2,
  DC: 3,
  TD: 3,
  TS: 3,
  POR: 4,
};

export function rolePriority(role: string | null | undefined): number {
  const r = (role || "").toUpperCase().trim();
  return ROLE_PRIORITY[r as PlayerRoleCode] ?? 5;
}

export function roleSectionLabel(priority: number): string {
  return tr(`roleSections.${priority}`, "");
}
